import type { ParseKeys } from 'i18next';
import type {
  EvaluationChart,
  RankingSubject,
  StudentAverageEvaluationsCharts,
  StudentDisciplineChart,
} from '@/types/ratings';
import { EvaluationChartType } from '@/types/ratings';
import { compareLatvian } from '@/utils/text';

const CHART_SCALES: Record<EvaluationChartType, { min: number; max: number }> =
  {
    [EvaluationChartType.Numeric]: { min: 0, max: 10 },
    [EvaluationChartType.Percent]: { min: 0, max: 100 },
    [EvaluationChartType.AchievableResult]: { min: 0, max: 4 },
    [EvaluationChartType.Weighted]: { min: 1, max: 10 },
  };

export const chartTypeLabelKeys: Record<EvaluationChartType, ParseKeys> = {
  [EvaluationChartType.Numeric]: 'overview.ratings.tabs.numeric',
  [EvaluationChartType.Percent]: 'overview.ratings.tabs.percent',
  [EvaluationChartType.AchievableResult]: 'overview.ratings.tabs.achievable',
  [EvaluationChartType.Weighted]: 'overview.ratings.tabs.weighted',
};

export const rankingTitleKeys: Record<EvaluationChartType, ParseKeys> = {
  [EvaluationChartType.Numeric]: 'overview.ratings.ranking.gradesTitle',
  [EvaluationChartType.Percent]: 'overview.ratings.ranking.percentTitle',
  [EvaluationChartType.AchievableResult]:
    'overview.ratings.ranking.achievableTitle',
  [EvaluationChartType.Weighted]: 'overview.ratings.ranking.gradesTitle',
};

export const findChartByType = (
  charts: EvaluationChart[],
  type: EvaluationChartType | null,
): EvaluationChart | undefined => charts.find((chart) => chart.type === type);

export const getRankingSubjects = (
  charts: StudentAverageEvaluationsCharts | null | undefined,
  allSubjectsLabel: string,
): RankingSubject[] => {
  const disciplines = (charts?.chartsByDiscipline ?? [])
    .map((chart) => ({
      id: chart.disciplineId,
      name: chart.disciplineName ?? '',
    }))
    .sort((a, b) => compareLatvian(a.name, b.name));

  return [{ id: null, name: allSubjectsLabel }, ...disciplines];
};

export const getRankingChart = (
  charts: StudentAverageEvaluationsCharts | null | undefined,
  subjectId: number | null,
): StudentDisciplineChart | null => {
  if (!charts) {
    return null;
  }

  if (subjectId === null) {
    return charts.summaryChartForAllDisciplines;
  }

  return (
    charts.chartsByDiscipline.find(
      (chart) => chart.disciplineId === subjectId,
    ) ?? null
  );
};

export const isAchievableChart = (type: EvaluationChartType): boolean =>
  type === EvaluationChartType.AchievableResult;

export const getValueColumnClass = (type: EvaluationChartType): string =>
  type === EvaluationChartType.Percent ? 'w-16' : 'w-12';

export const normalizeEvaluation = (
  value: number,
  type: EvaluationChartType,
): number => {
  const { min, max } = CHART_SCALES[type];
  const factor = (value - min) / (max - min);
  return Math.min(1, Math.max(0, factor));
};
